/**
 * Incognito Warning Modal Component
 *
 * CRITICAL blocking modal when incognito/private mode detected.
 *
 * DESIGN:
 * - Full-screen blocking modal (z-50)
 * - Warning icon with clear explanation
 * - Instructions to use normal browsing mode
 * - No close button (blocking)
 *
 * BUG FIXES:
 * - Bug #4: Two-method detection (quota + IndexedDB)
 * - Bug #11: Daily reset (not permanent dismiss)
 *
 * INTEGRATION:
 * - Add to TailwindApp.js startup check
 * - Uses detectIncognitoMode from incognitoDetection.js
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import { detectIncognitoMode } from '../../utils/incognitoDetection';
import { devLog } from '../../utils/devLog';

// Storage key for daily reset (Bug #11)
const DISMISS_KEY = 'sr.incognito.dismissedDate';
const DEV_BYPASS_KEY = 'sr.incognito.devBypass';

export function IncognitoWarningModal() {
  const [showWarning, setShowWarning] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [detectionDetails, setDetectionDetails] = useState(null);
  const [devBypassEnabled, setDevBypassEnabled] = useState(false);

  useEffect(() => {
    checkIncognitoMode();
  }, []);

  const checkIncognitoMode = async () => {
    try {
      // Check for developer bypass
      const bypass = localStorage.getItem(DEV_BYPASS_KEY);
      if (bypass === 'true' && process.env.NODE_ENV === 'development') {
        devLog('[INCOGNITO] Developer bypass enabled');
        setDevBypassEnabled(true);
        setIsChecking(false);
        return;
      }

      // Bug #11 Fix: Check if dismissed today (daily reset)
      const dismissedDate = localStorage.getItem(DISMISS_KEY);
      const today = new Date().toDateString();

      if (dismissedDate === today) {
        devLog('[INCOGNITO] Warning dismissed today, skipping check');
        setIsChecking(false);
        return;
      }

      // Run detection (Bug #4 - two-method detection)
      const result = await detectIncognitoMode();
      setDetectionDetails(result);

      if (result.isIncognito) {
        devLog('[INCOGNITO] Incognito mode detected, showing warning');
        setShowWarning(true);
      } else {
        devLog('[INCOGNITO] Normal browsing mode detected');
      }

      setIsChecking(false);

    } catch (error) {
      console.error('[INCOGNITO] Detection error:', error);
      setIsChecking(false);
    }
  };

  const handleDismissForToday = () => {
    // Bug #11 Fix: Daily reset, not permanent
    const today = new Date().toDateString();
    localStorage.setItem(DISMISS_KEY, today);
    setShowWarning(false);
  };

  const handleDevBypass = () => {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem(DEV_BYPASS_KEY, 'true');
      setDevBypassEnabled(true);
      setShowWarning(false);
    }
  };

  // Don't render anything while checking or if not showing
  if (isChecking || (!showWarning && !devBypassEnabled)) {
    return null;
  }

  // Developer bypass indicator (only in dev mode)
  if (devBypassEnabled && process.env.NODE_ENV === 'development') {
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-yellow-100 border-2 border-yellow-500 rounded-lg px-4 py-2 shadow-lg">
        <div className="flex items-center gap-2 text-sm text-yellow-800">
          <Info className="h-4 w-4" />
          <span className="font-medium">Incognito Detection: Dev Bypass Active</span>
          <button
            onClick={() => {
              localStorage.removeItem(DEV_BYPASS_KEY);
              setDevBypassEnabled(false);
              checkIncognitoMode();
            }}
            className="ml-2 text-xs underline hover:no-underline"
          >
            Re-enable
          </button>
        </div>
      </div>
    );
  }

  if (!showWarning) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-8 w-8" />
            <div>
              <h2 className="text-xl font-bold">Private Browsing Mode Detected</h2>
              <p className="text-red-100 text-sm mt-1">
                FleetSkipper requires normal browsing mode for data safety
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-2">
                  Why can't I use Private Browsing?
                </h3>
                <p className="text-red-800 text-sm leading-relaxed">
                  Private browsing modes (Incognito, Private Window) automatically delete all data when you close the tab.
                  This means your vessel records, safety logs, and compliance documentation would be permanently lost.
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Info className="h-5 w-5 text-cyan-600" />
              How to Switch to Normal Browsing
            </h3>

            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-start gap-2">
                <span className="font-semibold text-cyan-600 mt-0.5">Chrome:</span>
                <span>Open a new regular window (not Incognito)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-cyan-600 mt-0.5">Safari:</span>
                <span>Turn off Private Browsing mode (File → New Window)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-cyan-600 mt-0.5">Firefox:</span>
                <span>Open a new regular window (not Private Window)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-cyan-600 mt-0.5">Edge:</span>
                <span>Open a new regular window (not InPrivate)</span>
              </div>
            </div>
          </div>

          {/* Data Protection Assurance */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <h3 className="font-semibold text-cyan-900 mb-2 text-sm">
              Your data is protected
            </h3>
            <p className="text-cyan-800 text-sm leading-relaxed">
              FleetSkipper stores your vessel data securely on your device and syncs to the cloud.
              We use industry-standard encryption and never share your information with third parties.
            </p>
          </div>

          {/* Detection Details (Development only) */}
          {process.env.NODE_ENV === 'development' && detectionDetails && (
            <div className="bg-slate-100 border border-slate-300 rounded-lg p-4">
              <h4 className="font-semibold text-slate-700 text-xs mb-2">
                Detection Details (Dev Only)
              </h4>
              <pre className="text-xs text-slate-600 overflow-auto">
                {JSON.stringify(detectionDetails, null, 2)}
              </pre>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleDismissForToday}
              className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
            >
              Dismiss for Today
            </button>

            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={handleDevBypass}
                className="px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium text-sm"
              >
                Dev Bypass
              </button>
            )}

            <button
              onClick={() => window.location.reload()}
              className="flex-1 px-4 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
            >
              I've Switched - Reload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
